/**
 * Deterministic renderer for FROZEN Artwork canonical content.
 *
 * Reads ONLY the immutable canonical payload ({palette, rle}) returned by the
 * backend. It NEVER reads mutable Canvas state. Canonical convention:
 *   - symbol index in [0, palette.length-1]  → palette[index]
 *   - symbol index == palette.length         → TRANSPARENT sentinel
 * Row-major, exactly width × height cells.
 */
import { useEffect, useRef } from "react";
import type { PixelContent } from "../lib/artwork-api";

export function FrozenArtworkRender({
  content,
  width,
  height,
  maxPx = 320,
  className,
  testid,
}: {
  content: PixelContent;
  width: number;
  height: number;
  maxPx?: number;
  className?: string;
  testid?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const transparentIdx = content.palette.length;
    let cell = 0;
    const total = width * height;
    for (const run of content.rle) {
      const sym = run[0];
      const count = run[1];
      for (let k = 0; k < count && cell < total; k++, cell++) {
        if (sym !== transparentIdx) {
          const colour = content.palette[sym];
          if (colour) {
            ctx.fillStyle = colour;
            ctx.fillRect(cell % width, Math.floor(cell / width), 1, 1);
          }
        }
      }
    }
  }, [content, width, height]);

  const scale = Math.max(1, Math.floor(maxPx / Math.max(width, height)));
  const dispW = width * scale;
  const dispH = height * scale;

  return (
    <div
      className={className}
      data-testid={testid}
      style={{
        // Transparent-cell checkerboard so transparency is visibly distinct.
        backgroundColor: "#05070b",
        backgroundImage:
          "linear-gradient(45deg,#141922 25%,transparent 25%),linear-gradient(-45deg,#141922 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#141922 75%),linear-gradient(-45deg,transparent 75%,#141922 75%)",
        backgroundSize: "12px 12px",
        backgroundPosition: "0 0,0 6px,6px -6px,-6px 0",
        display: "inline-block",
        lineHeight: 0,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <canvas
        ref={ref}
        width={width}
        height={height}
        style={{ width: dispW, height: dispH, imageRendering: "pixelated", display: "block" }}
      />
    </div>
  );
}
